/**
 * @file CreatePassword.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-13
 * @modified 2026-07-27
 */

import { useState, useEffect } from 'react';
import './Login.css';

function CreatePassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userData, setUserData] = useState(null);
  const [theme] = useState(localStorage.getItem('theme') || 'dark');
  const [token, setToken] = useState('');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);

    // Get token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');

    if (!tokenParam) {
      setError('Invalid or missing token');
      setLoading(false);
      return;
    }

    setToken(tokenParam);
    verifyToken(tokenParam);
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch(`/api/users/verify-token/${tokenToVerify}`, {
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setTokenValid(true);
        setUserData(data.user);
      } else {
        setError(data.error || 'Invalid or expired token');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('at least 8 characters');
    if (!/[a-z]/.test(pwd)) errors.push('a lowercase letter');
    if (!/[A-Z]/.test(pwd)) errors.push('an uppercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('a number');
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) errors.push('a special character');
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validation
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(`Password must contain: ${passwordErrors.join(', ')}`);
      setSubmitting(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/users/create-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to create password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-container svg-bg">
        <div className="login-card">
          <div className="login-header">
            <img
              src={theme === 'light' ? '/excel-agri-logo-dark.svg' : '/excel-agri-logo.svg'}
              alt="Excel Agri"
              className="login-logo-icon"
            />
          </div>
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            Verifying token...
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-container svg-bg">
        <div className="login-card">
          <div className="login-header">
            <img
              src={theme === 'light' ? '/excel-agri-logo-dark.svg' : '/excel-agri-logo.svg'}
              alt="Excel Agri"
              className="login-logo-icon"
            />
          </div>

          <div className="error-message" style={{ margin: '20px' }}>
            {error}
          </div>

          <div style={{ textAlign: 'center', padding: '0 20px 20px' }}>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
              This link may have expired or is invalid. Please contact support for assistance.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => window.location.href = '/'}
                className="btn-primary"
              >
                <span className="material-symbols-rounded">arrow_back</span> Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container svg-bg">
        <div className="login-card">
          <div className="login-header">
            <img
              src={theme === 'light' ? '/excel-agri-logo-dark.svg' : '/excel-agri-logo.svg'}
              alt="Excel Agri"
              className="login-logo-icon"
            />
          </div>

          <div className="success-message" style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
              color: 'var(--color-primary)'
            }}>
              ✓
            </div>
            <h2 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>
              Password Created
            </h2>
            <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
              Your password has been created successfully. You can now log in with your email and password.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => window.location.href = '/'}
                className="btn-primary"
              >
                <span className="material-symbols-rounded">login</span> Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container svg-bg">
      <div className="login-card">
        <div className="login-header">
          <img
            src={theme === 'light' ? '/excel-agri-logo-dark.svg' : '/excel-agri-logo.svg'}
            alt="Excel Agri"
            className="login-logo-icon"
          />
          <h2 style={{ marginTop: '10px', color: 'var(--text-primary)' }}>
            Create Your Password
          </h2>
        </div>

        {userData && (
          <div style={{
            textAlign: 'center',
            padding: '10px 20px',
            backgroundColor: 'var(--bg-tertiary)',
            margin: '0 20px 20px',
            borderRadius: '8px',
            border: '1px solid var(--border-primary)'
          }}>
            <p style={{ color: 'var(--text-secondary)', margin: '5px 0' }}>
              Welcome, <strong>{userData.firstName} {userData.lastName}</strong>
            </p>
            <p style={{ color: 'var(--text-secondary)', margin: '5px 0', fontSize: '14px' }}>
              {userData.email}
            </p>
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <small style={{
              display: 'block',
              marginTop: '5px',
              color: 'var(--text-secondary)',
              fontSize: '12px'
            }}>
              At least 8 characters with uppercase, lowercase, number, and special character
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary signin-button"
            disabled={submitting}
          >
            {submitting ? 'Creating Password...' : 'Create Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CreatePassword;
